<template>
  <div class="case-illustration" :class="[typeClass, variantClass]">
    <img
      :src="illustrationSrc"
      :alt="altText"
      :loading="loadingMode"
      decoding="async"
      class="illustration-image"
    >
    <div class="motion-grid"></div>
    <div class="motion-glow glow-a"></div>
    <div class="motion-glow glow-b"></div>
    <div class="motion-particles">
      <span class="particle p-1"></span>
      <span class="particle p-2"></span>
      <span class="particle p-3"></span>
      <span class="particle p-4"></span>
    </div>
    <div class="variant-motion">
      <template v-if="props.type === 'gaming'">
        <span class="orbit orbit-outer"></span>
        <span class="orbit orbit-inner"></span>
      </template>
      <template v-else-if="props.type === 'education'">
        <span class="scan-line"></span>
      </template>
      <template v-else-if="props.type === 'health'">
        <span class="pulse-wave wave-1"></span>
        <span class="pulse-wave wave-2"></span>
      </template>
      <template v-else-if="props.type === 'automotive'">
        <span class="speed-line speed-1"></span>
        <span class="speed-line speed-2"></span>
        <span class="speed-line speed-3"></span>
      </template>
      <template v-else>
        <span class="focus-ring"></span>
      </template>
    </div>
    <div class="illustration-shade"></div>
    <div class="illustration-highlight"></div>
  </div>
</template>

<script setup>
const props = defineProps({
  type: {
    type: String,
    default: 'targeting',
    validator: (value) => ['shopping', 'gaming', 'education', 'health', 'automotive', 'targeting'].includes(value)
  },
  variant: {
    type: String,
    default: ''
  }
})

const resolvedKey = computed(() => props.variant || props.type || 'targeting')
const typeClass = computed(() => `type-${props.type || 'targeting'}`)
const variantClass = computed(() => (props.variant ? `variant-${props.variant}` : ''))

const illustrations = {
  shopping: '/visuals/cases/case-1-ecommerce.svg',
  gaming: '/visuals/cases/case-2-gaming.svg',
  education: '/visuals/cases/case-3-education.svg',
  health: '/visuals/cases/case-5-health.svg',
  automotive: '/visuals/cases/case-6-automotive.svg',
  targeting: '/visuals/cases/case-4-commerce.svg',
  'case-1': '/visuals/cases/case-1-ecommerce.svg',
  'case-2': '/visuals/cases/case-2-gaming.svg',
  'case-3': '/visuals/cases/case-3-education.svg',
  'case-4': '/visuals/cases/case-4-commerce.svg',
  'case-5': '/visuals/cases/case-5-health.svg',
  'case-6': '/visuals/cases/case-6-automotive.svg'
}

const altLabels = {
  shopping: '电商增长案例头图',
  gaming: '游戏行业案例头图',
  education: '在线教育案例头图',
  health: '医疗健康案例头图',
  automotive: '汽车行业案例头图',
  targeting: '数字营销案例头图',
  'case-1': '电商平台精准营销案例头图',
  'case-2': '游戏行业增长案例头图',
  'case-3': '在线教育增长案例头图',
  'case-4': '电商收益优化案例头图',
  'case-5': '医疗健康出海案例头图',
  'case-6': '汽车线索增长案例头图'
}

const illustrationSrc = computed(() => illustrations[resolvedKey.value] || illustrations[props.type] || illustrations.targeting)
const altText = computed(() => altLabels[resolvedKey.value] || altLabels[props.type] || altLabels.targeting)
const loadingMode = computed(() => (props.variant ? 'lazy' : 'eager'))
</script>

<style scoped>
.case-illustration {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 1rem;
  border: 1px solid rgba(99, 102, 241, 0.28);
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.82), rgba(30, 58, 138, 0.56));
  box-shadow: 0 16px 42px rgba(2, 6, 23, 0.32);
  isolation: isolate;
}

.illustration-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.02);
  animation: illustration-drift 15s ease-in-out infinite alternate;
}

.motion-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(147, 197, 253, 0.14) 1px, transparent 1px),
    linear-gradient(90deg, rgba(147, 197, 253, 0.12) 1px, transparent 1px);
  background-size: 28px 28px;
  opacity: 0.16;
  animation: grid-shift 20s linear infinite;
  pointer-events: none;
}

.motion-glow {
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 9999px;
  filter: blur(38px);
  opacity: 0.26;
  pointer-events: none;
}

.glow-a {
  top: -42px;
  left: 10%;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.7), rgba(56, 189, 248, 0));
  animation: glow-float 10s ease-in-out infinite alternate;
}

.glow-b {
  right: 7%;
  bottom: -46px;
  background: radial-gradient(circle, rgba(167, 139, 250, 0.7), rgba(167, 139, 250, 0));
  animation: glow-float 12s ease-in-out infinite alternate-reverse;
}

.motion-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #93C5FD, #A78BFA);
  opacity: 0.48;
  animation: particle-drift 9s ease-in-out infinite;
}

.p-1 {
  left: 14%;
  top: 18%;
}

.p-2 {
  left: 76%;
  top: 22%;
  animation-delay: -1.4s;
}

.p-3 {
  left: 24%;
  top: 76%;
  animation-delay: -2.2s;
}

.p-4 {
  left: 86%;
  top: 68%;
  animation-delay: -3.1s;
}

.variant-motion {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.orbit {
  position: absolute;
  left: 50%;
  top: 50%;
  border: 1px dashed rgba(167, 139, 250, 0.42);
  border-radius: 9999px;
}

.orbit-outer {
  width: 58%;
  height: 58%;
  transform: translate(-50%, -50%);
  animation: orbit-spin 16s linear infinite;
}

.orbit-inner {
  width: 40%;
  height: 40%;
  transform: translate(-50%, -50%);
  border-color: rgba(96, 165, 250, 0.46);
  animation: orbit-spin 12s linear infinite reverse;
}

.scan-line {
  position: absolute;
  left: 12%;
  right: 12%;
  height: 2px;
  border-radius: 9999px;
  top: 44%;
  background: linear-gradient(90deg, transparent, rgba(147, 197, 253, 0.92), rgba(167, 139, 250, 0.92), transparent);
  box-shadow: 0 0 14px rgba(96, 165, 250, 0.42);
  animation: scan-move 4.8s ease-in-out infinite;
}

.pulse-wave {
  position: absolute;
  left: 50%;
  top: 50%;
  border: 1px solid rgba(96, 165, 250, 0.38);
  border-radius: 9999px;
  transform: translate(-50%, -50%) scale(0.8);
  opacity: 0;
}

.wave-1 {
  width: 34%;
  height: 34%;
  animation: pulse-ring 3.8s ease-out infinite;
}

.wave-2 {
  width: 46%;
  height: 46%;
  border-color: rgba(167, 139, 250, 0.36);
  animation: pulse-ring 3.8s ease-out infinite 1.1s;
}

.speed-line {
  position: absolute;
  left: 10%;
  width: 30%;
  height: 2px;
  border-radius: 9999px;
  background: linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.82), rgba(167, 139, 250, 0.82), transparent);
  opacity: 0;
}

.speed-1 {
  top: 34%;
  animation: speed-flow 2.6s ease-in-out infinite;
}

.speed-2 {
  top: 49%;
  animation: speed-flow 2.6s ease-in-out infinite 0.45s;
}

.speed-3 {
  top: 64%;
  animation: speed-flow 2.6s ease-in-out infinite 0.9s;
}

.focus-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 44%;
  height: 44%;
  border: 1px solid rgba(125, 211, 252, 0.36);
  border-radius: 9999px;
  transform: translate(-50%, -50%);
  animation: ring-breathe 6s ease-in-out infinite;
}

.illustration-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(15, 23, 42, 0.2) 100%),
    radial-gradient(circle at 12% 8%, rgba(56, 189, 248, 0.14), transparent 40%);
  pointer-events: none;
}

.illustration-highlight {
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(255, 255, 255, 0.16), transparent 32%, transparent 68%, rgba(167, 139, 250, 0.12));
  mix-blend-mode: screen;
  opacity: 0.42;
  pointer-events: none;
}

.type-gaming .illustration-image {
  animation-duration: 17s;
}

.type-education .illustration-image {
  animation-duration: 13s;
}

.type-health .illustration-image {
  animation-duration: 16s;
}

.dark .case-illustration {
  border-color: rgba(125, 211, 252, 0.22);
}

.light .case-illustration {
  border-color: rgba(59, 130, 246, 0.24);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(224, 242, 254, 0.78));
  box-shadow: 0 16px 38px rgba(30, 64, 175, 0.14);
}

.light .illustration-shade {
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(15, 23, 42, 0.05) 100%),
    radial-gradient(circle at 10% 10%, rgba(56, 189, 248, 0.11), transparent 40%);
}

.light .motion-grid {
  opacity: 0.18;
}

.light .motion-glow {
  opacity: 0.22;
}

.light .particle {
  opacity: 0.4;
}

@keyframes illustration-drift {
  0% {
    transform: translate3d(0, 0, 0) scale(1.02);
  }
  50% {
    transform: translate3d(-1.1%, 1.2%, 0) scale(1.04);
  }
  100% {
    transform: translate3d(1.2%, -1.1%, 0) scale(1.02);
  }
}

@keyframes grid-shift {
  0% {
    background-position: 0 0, 0 0;
  }
  100% {
    background-position: 0 28px, 28px 0;
  }
}

@keyframes glow-float {
  0% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  100% {
    transform: translate3d(12px, -10px, 0) scale(1.08);
  }
}

@keyframes particle-drift {
  0%, 100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(0, -12px, 0) scale(1.08);
  }
}

@keyframes orbit-spin {
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes scan-move {
  0%, 100% {
    transform: translateY(-24px);
    opacity: 0.18;
  }
  50% {
    transform: translateY(24px);
    opacity: 0.7;
  }
}

@keyframes pulse-ring {
  0% {
    transform: translate(-50%, -50%) scale(0.74);
    opacity: 0.56;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.22);
    opacity: 0;
  }
}

@keyframes speed-flow {
  0% {
    transform: translateX(-24px);
    opacity: 0;
  }
  45% {
    opacity: 0.78;
  }
  100% {
    transform: translateX(60px);
    opacity: 0;
  }
}

@keyframes ring-breathe {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.28;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.08);
    opacity: 0.54;
  }
}

@media (prefers-reduced-motion: reduce) {
  .illustration-image {
    animation: none;
    transform: scale(1.01);
  }

  .motion-grid,
  .motion-glow,
  .particle,
  .orbit,
  .scan-line,
  .pulse-wave,
  .speed-line,
  .focus-ring {
    animation: none !important;
  }
}
</style>
